import React, { useRef, useState, useLayoutEffect, useCallback, useEffect } from "react";
import backArrow from "../../assets/icons/back-arrow.svg";
import nextArrow from "../../assets/icons/next-arrow.svg";
import { CarouselProps } from '../../types/CarouselProps';

const BUTTON_CLASSES = "absolute top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-lg w-8 h-8 flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.12)] transition-all duration-200";

const Carousel: React.FC<CarouselProps> = ({
    children,
    cardWidth = 250,
    className = "",
}) => {
    const [carouselState, setCarouselState] = useState({
        currentIndex: 0,
        cardsPerView: 3,
        touchStart: null as number | null,
        touchEnd: null as number | null,
    });
    const viewportRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [childWidth, setChildWidth] = React.useState<number>(cardWidth);

    const updateCardsPerView = useCallback(() => {
        if (!viewportRef.current) return;
        const width = viewportRef.current.offsetWidth;
        const cards = Math.floor(width / (childWidth + 16));
        setCarouselState(prev => {
            const newCardsPerView = Math.min(3, Math.max(1, cards));
            const maxIndex = Math.max(0, children.length - newCardsPerView);
            const newCurrentIndex = Math.min(prev.currentIndex, maxIndex);
            return { ...prev, cardsPerView: newCardsPerView, currentIndex: newCurrentIndex };
        });
    }, [childWidth, children.length]);

    useLayoutEffect(() => {
        const updateChildWidth = () => {
            if (!containerRef.current || containerRef.current.children.length === 0) return;
            const firstChild = containerRef.current.children[0] as HTMLElement;
            const measured = Math.round(firstChild.getBoundingClientRect().width);
            if (measured > 0 && measured !== childWidth) {
                setChildWidth(measured);
            }
        };

        updateChildWidth();

        const ro = new ResizeObserver(updateChildWidth);
        if (containerRef.current) ro.observe(containerRef.current);

        window.addEventListener("resize", updateChildWidth);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", updateChildWidth);
        };
    }, [containerRef, childWidth]);

    useLayoutEffect(() => {
        updateCardsPerView();
    }, [updateCardsPerView]);

    useEffect(() => {
        const handleResize = () => updateCardsPerView();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [updateCardsPerView]);

    const nextSlide = () => {
        setCarouselState(prev => {
            if (!viewportRef.current || !containerRef.current) {
                return { ...prev, currentIndex: prev.currentIndex + prev.cardsPerView };
            }

            const containerWidth = children.length * (childWidth + 16) - 16;
            const viewportWidth = viewportRef.current.offsetWidth;
            const maxTranslate = Math.max(0, containerWidth - viewportWidth);
            const maxIndex = Math.ceil(maxTranslate / (childWidth + 16));

            const newIndex = Math.min(prev.currentIndex + prev.cardsPerView, maxIndex);
            return { ...prev, currentIndex: newIndex };
        });
    };

    const prevSlide = () => {
        setCarouselState(prev => ({
            ...prev,
            currentIndex: Math.max(0, prev.currentIndex - prev.cardsPerView)
        }));
    };

    const onTouchStart = (e: React.TouchEvent) => {
        setCarouselState(prev => ({ ...prev, touchEnd: null, touchStart: e.targetTouches[0].clientX }));
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setCarouselState(prev => ({ ...prev, touchEnd: e.targetTouches[0].clientX }));
    };

    const onTouchEnd = () => {
        if (!carouselState.touchStart || !carouselState.touchEnd) return;

        const distance = carouselState.touchStart - carouselState.touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            const containerWidth = children.length * (childWidth + 16) - 16;
            const viewportWidth = viewportRef.current ? viewportRef.current.offsetWidth : 0;
            const maxTranslate = Math.max(0, containerWidth - viewportWidth);
            const maxIndex = Math.ceil(maxTranslate / (childWidth + 16));
            if (carouselState.currentIndex < maxIndex) nextSlide();
        }
        if (isRightSwipe && carouselState.currentIndex > 0) {
            prevSlide();
        }
    };

    const showNavigation = children.length > carouselState.cardsPerView;
    const containerWidth = children.length * (childWidth + 16) - 16;

    let translateX = carouselState.currentIndex * (childWidth + 16);
    if (viewportRef.current) {
        const viewportWidth = viewportRef.current.offsetWidth;
        const maxTranslate = Math.max(0, containerWidth - viewportWidth);
        translateX = Math.min(translateX, maxTranslate);
    }

    return (
        <div className={`relative ${className}`}>
            <div ref={viewportRef} className="overflow-hidden">
                <div
                    ref={containerRef}
                    className="flex gap-4 transition-transform duration-500 ease-out"
                    style={{
                        transform: `translateX(-${translateX}px)`,
                        width: `${containerWidth}px`,
                    }}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {children}
                </div>
            </div>
            {showNavigation && (
                <>
                    {carouselState.currentIndex > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                            className={`${BUTTON_CLASSES} left-0`}
                            aria-label="Previous"
                        >
                            <img src={backArrow} alt="Previous" className="w-4 h-4" />
                        </button>
                    )}
                    {(() => {
                        // Show the next arrow only if we can move forward to see new content
                        const viewportWidth = viewportRef.current ? viewportRef.current.offsetWidth : 0;
                        const maxTranslate = Math.max(0, containerWidth - viewportWidth);
                        const maxIndex = Math.ceil(maxTranslate / (childWidth + 16));
                        return carouselState.currentIndex < maxIndex;
                    })() && (
                            <button
                                onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                                className={`${BUTTON_CLASSES} right-0`}
                                aria-label="Next"
                            >
                                <img src={nextArrow} alt="Next" className="w-4 h-4" />
                            </button>
                        )}
                </>
            )}
        </div>
    );
};

export default Carousel;
